import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Option 2: Glassmorphism/Modern Style
 * Heavy blur, gradients, contemporary look
 */
const GlassProgress = ({ currentStep = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    { id: 1, name: 'Business Context' },
    { id: 2, name: 'Table Selection' },
    { id: 3, name: 'Domain Classification' },
    { id: 4, name: 'Data Profiling' },
    { id: 5, name: 'Relationship Identification' },
    { id: 6, name: 'Business Glossary' },
  ];

  const currentStepInfo = steps[currentStep - 1];
  const progressPercentage = ((currentStep - 1) / 5) * 100;

  return (
    <div
      className="absolute top-4 left-4 z-50 transition-all duration-300"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Compact View */}
      {!isExpanded && (
        <div 
          className="backdrop-blur-2xl bg-white/5 border border-white/20 rounded-2xl px-4 py-2.5 shadow-2xl"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Step {currentStep} of {steps.length}
              </span>
              <span className="text-[10px] text-white/70">
                {currentStepInfo?.name}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div 
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-2xl px-5 py-4 shadow-2xl min-w-[300px]"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold text-white/90">Generation Progress</span>
            <div className="px-2 py-0.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[10px] font-bold">
              {currentStep}/{steps.length}
            </div>
          </div>

          {/* Glass Progress Bar */}
          <div className="mb-4">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 transition-all duration-700 ease-out rounded-full"
                style={{ 
                  width: `${progressPercentage}%`,
                  boxShadow: '0 0 10px rgba(96, 165, 250, 0.5)'
                }}
              />
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-2.5">
            {steps.map((step) => {
              const isComplete = step.id < currentStep;
              const isCurrent = step.id === currentStep;
              const isPending = step.id > currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 text-xs transition-all duration-300 ${
                    isCurrent ? 'scale-105' : ''
                  }`}
                >
                  {isComplete && (
                    <>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-white/50 line-through">{step.name}</span>
                    </>
                  )}
                  {isCurrent && (
                    <>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg animate-pulse">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-white font-medium">{step.name}</span>
                    </>
                  )}
                  {isPending && (
                    <>
                      <div className="w-5 h-5 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white/30" />
                      </div>
                      <span className="text-white/40">{step.name}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlassProgress;